import { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

// Founder 2026-09-20 (staging review): a small round button on the map's right
// edge, above the card row's right arrow, that hides the three tabs (with their
// open summary strip) and the bottom card row for a map-only view, and shows
// them again on the next click. The button itself stays visible in both
// states. The map is not touched: the panels are hidden with CSS, never
// unmounted, so their state and the map's position and layers survive.
//
// Founder 2026-09-21 (production review): the button was not discoverable. A
// "Full map" label appears beside it on load, pulses once and fades after
// 5 s; on devices with hover the label comes back as a tooltip. Once the
// button has been clicked on a device the on-load label no longer appears
// (localStorage flag, read and written inside try/catch - private mode or
// blocked storage just means the label shows every time).
//
// Styling mirrors the card strip's slick arrows (src/styles/sliderArrows.ts):
// 34px white disc, same shadow. Positioned just above the card row (Cards
// Container: bottom 20px desktop / 5px under 400px wide; a card is ~150px tall
// on desktop and ~105px on a phone).
export const PANELS_TOGGLE_USED_KEY = 'panelsToggleUsed';
export const PANELS_LABEL_MS = 5000;
export const PANELS_LABEL_TEXT = 'Full map';

const readUsed = (): boolean => {
  try {
    return window.localStorage.getItem(PANELS_TOGGLE_USED_KEY) === '1';
  } catch {
    return false;
  }
};
const writeUsed = () => {
  try {
    window.localStorage.setItem(PANELS_TOGGLE_USED_KEY, '1');
  } catch {
    // storage blocked: the label simply shows again next time
  }
};

const Wrap = styled.div`
  position: absolute;
  right: 8px;
  bottom: 190px;
  z-index: 101; /* above the card strip (100) */
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 400px) {
    bottom: 125px;
  }
`;

const Button = styled.button`
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:focus-visible {
    outline: 3px solid #84a341;
    outline-offset: 2px;
  }
`;

const pulseOnce = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.12); }
  100% { transform: scale(1); }
`;

// The label sits to the LEFT of the button (the button hugs the map's right
// edge). It fades with a transition so the 5 s hide is not a pop.
const Label = styled.span<{ $visible: boolean; $pulse: boolean }>`
  background: rgba(0, 0, 0, 0.7);
  color: #ffffff;
  font-size: 12px;
  line-height: 1;
  padding: 6px 8px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 600ms ease;
  animation: ${({ $pulse }) => ($pulse ? pulseOnce : 'none')} 700ms ease 1;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

export const PanelsToggle = ({ hidden, onToggle }: { hidden: boolean; onToggle: () => void }) => {
  // On-load label: only on a device where the button has never been used.
  const [onLoad, setOnLoad] = useState(() => !readUsed());
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (!onLoad) return;
    const t = window.setTimeout(() => setOnLoad(false), PANELS_LABEL_MS);
    return () => window.clearTimeout(t);
  }, [onLoad]);

  const visible = onLoad || hover;
  const click = () => {
    writeUsed();
    setOnLoad(false);
    onToggle();
  };

  return (
    <Wrap>
      <Label data-testid="toggle-cards-label" aria-hidden="true" $visible={visible} $pulse={onLoad}>
        {PANELS_LABEL_TEXT}
      </Label>
      <Button
        type="button"
        data-testid="toggle-cards"
        aria-label={hidden ? 'Show panels' : 'Hide panels'}
        aria-pressed={hidden}
        title={PANELS_LABEL_TEXT}
        onClick={click}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
      >
        {/* Chevron down = collapse the panels, chevron up = bring them back. */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#333333"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          {hidden ? <path d="M6 15l6-6 6 6" /> : <path d="M6 9l6 6 6-6" />}
        </svg>
      </Button>
    </Wrap>
  );
};
